import { useState, type Ref } from 'react'
import { Box, Flex, Text, VStack, Grid, Input, Spinner, useT } from '@minetrace/ui'
import {
  Controller,
  useFormContext,
  useFormColors,
  FormSection,
  FormLabel,
  TrackedInput,
  TrackedPhoneInput,
  withFormProvider,
  type WithFormProvider,
} from '@minetrace/ui/forms'
import {
  LuBuilding2 as Building2,
  LuSearch as Search,
} from 'react-icons/lu'
import type { CompanyPersonResponse } from '../../../api/models/response'
import type { AddCompanyPersonRequest } from '../../../api/models/request/company'
import type { CreateMiningRightRequest } from '../../../api/models/request/proveedor'
import type { RecpoRecordResponse, SinCapacidadOperativaResponse } from '../../../api/models/response/consultarRuc'
import { proveedoresService } from '../../../api/services'
import { useRequest } from '../../../hooks'
import { lookupUbigeoCode } from '../../../utils/ubigeoLookup'
import { miningRightStatusFromEstado } from '../../../lib/miningRightStatus'
import { toaster } from '@minetrace/ui'
import ContactosSection from '../../../components/ContactosSection'
import UbigeoSelect from '../../../components/UbigeoSelect'
import CharCounter from '../../../components/CharCounter'
import { CompanyFieldLengths } from '../../../lib/fieldLengths'
import {
  useStep1IdentityForm,
  type Step1IdentityFormValues,
} from './useStep1IdentityForm'

// Formatea fecha (ISO datetime o "yyyy-MM-dd") a dd/mm/aaaa local; "—" si falta.
const fmtSscoDate = (s?: string | null) => {
  if (!s) return '—'
  const d = new Date(s.length <= 10 ? `${s}T00:00:00` : s)
  return isNaN(d.getTime()) ? s : d.toLocaleDateString('es-PE')
}

// Data that Step1 produces for the parent (non-form data)
export interface Step1ConsultaResult {
  // RUC the user just consulted — surfaced so the parent can drive PJ-only
  // sections (e.g. PowerValiditiesSection, gated on RUC starting with "20")
  // without having to subscribe to the inner form's watch().
  ruc: string
  economicActivities: string[]
  taxpayerStatus: string | null
  taxpayerCondition: string | null
  taxpayerType: string | null
  /** Fecha de inicio de actividades que publica SUNAT. Se guarda en el paso de
      Acreditacion; el detalle la muestra y hasta ahora salia siempre vacia porque
      nadie la capturaba ni la arrastraba desde la consulta. */
  activityStartDate: string | null
  miningRights: CreateMiningRightRequest[]
  recpoRecords: RecpoRecordResponse[]
  minerSource: string | null
  reinfoSyncedAt: string | null
  /** Cuándo se consultó cada fuente oficial. Sin esto el backend guardaba null y se
      perdía la evidencia de la debida diligencia (fecha de consulta SUNAT/REINFO/RECPO). */
  sourceTimestamps: {
    sunatSyncedAt?: string
    sunatQueriedAt?: string
    recpoSyncedAt?: string
    recpoQueriedAt?: string
    reinfoQueriedAt?: string
  }
  sinCapacidadOperativa: SinCapacidadOperativaResponse | null
}

interface Step1IdentityProps {
  mode: 'edit' | 'readonly'
  defaultValues?: Partial<Step1IdentityFormValues>
  onConsultaCompleted?: (result: Step1ConsultaResult) => void
  // Pre-loaded data (from parent's proveedor response for display)
  initialConsultaData?: Partial<Step1ConsultaResult>
  // Contactos
  companyId: string | null
  persons: CompanyPersonResponse[]
  onPersonsChange: (persons: CompanyPersonResponse[]) => void
  addPerson: (id: string, data: AddCompanyPersonRequest) => Promise<CompanyPersonResponse>
  removePerson: (id: string, companyPersonId: string) => Promise<void>
  onPendingContactAdd?: (localId: string, request: AddCompanyPersonRequest) => void
  onPendingContactRemove?: (localId: string) => void
  // Ref for parent to access form methods (trigger, getValues)
  ref?: Ref<unknown>
}

// ── Inner form component ──

type IdentityFormInnerProps = Step1IdentityProps & WithFormProvider<Step1IdentityFormValues>

function IdentityFormInnerComponent({
  onConsultaCompleted,
  initialConsultaData,
  companyId,
  persons,
  onPersonsChange,
  addPerson,
  removePerson,
  onPendingContactAdd,
  onPendingContactRemove,
}: IdentityFormInnerProps) {
  const c = useFormColors()
  const { t } = useT()
  const { control, register, watch, setValue } = useFormContext<Step1IdentityFormValues>()
  const rucValue = watch('ruc')
  const rucLocked = !!companyId

  // SUNAT display state (managed internally)
  const [sunatConsulted, setSunatConsulted] = useState(() => Boolean(initialConsultaData?.taxpayerStatus))
  const [sunatFields, setSunatFields] = useState<Set<string>>(new Set())
  const [economicActivities, setEconomicActivities] = useState<string[]>(initialConsultaData?.economicActivities ?? [])
  const [taxpayerStatus, setTaxpayerStatus] = useState<string | null>(initialConsultaData?.taxpayerStatus ?? null)
  const [taxpayerCondition, setTaxpayerCondition] = useState<string | null>(initialConsultaData?.taxpayerCondition ?? null)
  const [taxpayerType, setTaxpayerType] = useState<string | null>(initialConsultaData?.taxpayerType ?? null)
  const [notAMinerError, setNotAMinerError] = useState(false)
  // SUNAT (scraping detrás de Cloudflare) falla de forma intermitente. Sin esto la
  // razón social —obligatoria y de solo lectura— quedaba vacía y el paso 1 bloqueado
  // sin ningún aviso. 'reinfo' = nombre tomado del REINFO; 'manual' = lo escribe el usuario.
  const [sunatUnavailable, setSunatUnavailable] = useState<null | 'reinfo' | 'manual'>(null)
  const [sinCapacidad, setSinCapacidad] = useState<SinCapacidadOperativaResponse | null>(
    initialConsultaData?.sinCapacidadOperativa ?? null,
  )

  // API hook
  const { execute: consultarRuc, isFetching: isSearchingRuc } = useRequest(proveedoresService.consultarRuc)

  // Consultar RUC — lives here, writes directly to form
  const handleConsultarRuc = async () => {
    if (!rucValue || rucValue.length !== 11) return
    setNotAMinerError(false)
    const data = await consultarRuc(rucValue)
    if (!data) return

    if (!data.minerSource) {
      const msg = t('proveedores.wizard.toasts.rucNotAMiner')
      toaster.error({ description: msg })
      setNotAMinerError(true)
      setSunatUnavailable(null)
      setSunatConsulted(false)
      setSunatFields(new Set())
      setEconomicActivities([])
      setTaxpayerStatus(null)
      setTaxpayerCondition(null)
      setTaxpayerType(null)
      setSinCapacidad(null)
      return
    }

    // Verificación SUNAT "Sin Capacidad Operativa" (SSCO): se notifica pero no bloquea.
    setSinCapacidad(data.sinCapacidadOperativa)
    if (data.sinCapacidadOperativa?.found)
      toaster.error({ description: t('proveedores.wizard.step1.sscoWarningTitle') })

    // Populate form fields from SUNAT
    if (data.sunat) {
      const fields = new Set<string>()
      if (data.sunat.razonSocial) { setValue('legalName', data.sunat.razonSocial); fields.add('legalName') }
      if (data.sunat.nombreComercial) { setValue('commercialName', data.sunat.nombreComercial); fields.add('commercialName') }
      if (data.sunat.direccion) { setValue('taxAddress', data.sunat.direccion); fields.add('taxAddress') }
      setSunatFields(fields)
      if (data.sunat.actividadesEconomicas?.length > 0) setEconomicActivities(data.sunat.actividadesEconomicas)
      setTaxpayerStatus(data.sunat.estadoContribuyente)
      setTaxpayerCondition(data.sunat.condicionContribuyente)
      setTaxpayerType(data.sunat.tipoContribuyente)
      setSunatConsulted(true)
      setSunatUnavailable(null)
    } else {
      const reinfoName = data.reinfo?.razonSocial?.trim()
      if (reinfoName) setValue('legalName', reinfoName, { shouldValidate: true })
      setSunatUnavailable(reinfoName ? 'reinfo' : 'manual')
      setSunatConsulted(false)
      setSunatFields(new Set())
      toaster.error({ description: t('proveedores.wizard.step1.sunatUnavailableTitle') })
    }

    // Build mining rights from REINFO
    let miningRights: CreateMiningRightRequest[] = []
    if (data.reinfo?.derechosMineros?.length) {
      miningRights = data.reinfo.derechosMineros.map(r => ({
        uniqueCode: r.uniqueCode ?? '',
        name: r.name ?? '',
        ubigeoCode: lookupUbigeoCode(r.department, r.province, r.district),
        miningRightStatusId: miningRightStatusFromEstado(r.estado),
        // Estado de formalización por derecho: REINFO = en vías de formalización; minero formal = formalizado.
        formalizationStatus: r.formalizationStatus ?? undefined,
        reinfoStatus: r.estado ?? undefined,
      }))
      toaster.success({ description: t('proveedores.wizard.toasts.miningRightsFound', { count: miningRights.length }) })
    }

    // Notify parent with non-form data
    onConsultaCompleted?.({
      ruc: rucValue,
      economicActivities: data.sunat?.actividadesEconomicas ?? [],
      taxpayerStatus: data.sunat?.estadoContribuyente ?? null,
      taxpayerCondition: data.sunat?.condicionContribuyente ?? null,
      taxpayerType: data.sunat?.tipoContribuyente ?? null,
      activityStartDate: data.sunat?.fechaInicioActividades ?? null,
      miningRights,
      recpoRecords: data.recpo ?? [],
      minerSource: data.minerSource,
      reinfoSyncedAt: data.reinfoSyncedAt ?? null,
      sourceTimestamps: {
        // Sólo se registra la consulta a SUNAT si efectivamente devolvió datos.
        sunatSyncedAt: data.sunat ? data.sunatSyncedAt ?? undefined : undefined,
        sunatQueriedAt: data.sunat ? data.sunatQueriedAt ?? undefined : undefined,
        recpoSyncedAt: data.recpoSyncedAt ?? undefined,
        recpoQueriedAt: data.recpoQueriedAt ?? undefined,
        reinfoQueriedAt: data.reinfoQueriedAt ?? undefined,
      },
      sinCapacidadOperativa: data.sinCapacidadOperativa ?? null,
    })
  }

  return (
    <VStack gap={8} alignItems="stretch">
      <FormSection icon={Building2} title={t('proveedores.wizard.step1.sectionTitle')}>
        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
          {/* RUC with SUNAT button */}
          <Box>
            <FormLabel tid="proveedores.wizard.step1.rucLabel" required />
            <Flex gap={2}>
              <Controller
                name="ruc"
                control={control}
                render={({ field }) => (
                  <Input
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value.replace(/[^0-9]/g, '').slice(0, CompanyFieldLengths.ruc))}
                    placeholder={t('proveedores.wizard.step1.rucPlaceholder')}
                    maxLength={CompanyFieldLengths.ruc}
                    inputMode="numeric"
                    // Con la empresa ya creada el RUC no cambia: otro RUC es otra empresa. En una
                    // solicitud de modificación se podía editar y consultar otro RUC, que pintaba
                    // otra razón social en pantalla sin stagear nada.
                    readOnly={rucLocked} disabled={rucLocked} opacity={rucLocked ? 0.7 : 1}
                  />
                )}
              />
              <Box
                as="button" type="button" display="flex" alignItems="center" justifyContent="center"
                px={4} bg={c.accent} color="white" borderRadius="sm"
                cursor={rucValue?.length !== 11 || isSearchingRuc || rucLocked ? 'not-allowed' : 'pointer'}
                opacity={rucValue?.length !== 11 || isSearchingRuc || rucLocked ? 0.5 : 1}
                flexShrink={0} minW="48px"
                onClick={rucLocked ? undefined : handleConsultarRuc}
                disabled={rucValue?.length !== 11 || isSearchingRuc || rucLocked}
              >
                {isSearchingRuc ? <Spinner size="sm" color="white" /> : <Search size={18} />}
              </Box>
            </Flex>
            {notAMinerError && (
              <Box
                mt={2} p={3} borderRadius="sm"
                bg="rgba(200, 68, 47, 0.08)"
                border="1px solid" borderColor="rgba(200, 68, 47, 0.3)"
              >
                <Text fontSize="sm" color="#9a3020" fontWeight="500">
                  {t('proveedores.wizard.toasts.rucNotAMiner')}
                </Text>
              </Box>
            )}
          </Box>

          {/* Verificación SUNAT "Sin Capacidad Operativa" (SSCO): SIEMPRE visible tras
              consultar (o al reabrir el proveedor). Rojo = figura, verde = no figura,
              gris = no se pudo verificar el padrón. Notifica pero no bloquea. */}
          {sinCapacidad && (
            <Box
              gridColumn={{ md: 'span 2' }} p={4} borderRadius="sm"
              bg={sinCapacidad.found ? 'rgba(200, 68, 47, 0.10)' : sinCapacidad.sourceAvailable ? 'rgba(42, 138, 135, 0.10)' : c.bgInput}
              border="1px solid"
              borderColor={sinCapacidad.found ? 'rgba(200, 68, 47, 0.4)' : sinCapacidad.sourceAvailable ? 'rgba(42, 138, 135, 0.4)' : c.border}
            >
              <Text fontSize="sm" fontWeight="700" color={sinCapacidad.found ? '#9a3020' : sinCapacidad.sourceAvailable ? '#1d6664' : c.textMuted}>
                {sinCapacidad.found
                  ? t('proveedores.wizard.step1.sscoWarningTitle')
                  : sinCapacidad.sourceAvailable
                    ? t('proveedores.wizard.step1.sscoClearTitle')
                    : t('proveedores.wizard.step1.sscoUnavailableTitle')}
              </Text>
              {sinCapacidad.found && (
                <Text fontSize="sm" color="#9a3020" mt={1}>
                  {t('proveedores.wizard.step1.sscoWarningBody', { date: fmtSscoDate(sinCapacidad.padronDate) })}
                </Text>
              )}
              <Text fontSize="xs" color={c.textMuted} mt={1}>
                {sinCapacidad.padronDate
                  ? t('proveedores.wizard.step1.sscoCheckedAt', { date: fmtSscoDate(sinCapacidad.checkedAt), padron: fmtSscoDate(sinCapacidad.padronDate) })
                  : t('proveedores.wizard.step1.sscoCheckedAtNoPadron', { date: fmtSscoDate(sinCapacidad.checkedAt) })}
              </Text>
            </Box>
          )}

          {sunatUnavailable && (
            <Box
              gridColumn={{ md: 'span 2' }} p={4} borderRadius="sm"
              bg="rgba(212, 154, 42, 0.10)" border="1px solid" borderColor="rgba(212, 154, 42, 0.45)"
            >
              <Text fontSize="sm" fontWeight="700" color="#8a5f0b">
                {t('proveedores.wizard.step1.sunatUnavailableTitle')}
              </Text>
              <Text fontSize="sm" color="#92400E" mt={1}>
                {sunatUnavailable === 'reinfo'
                  ? t('proveedores.wizard.step1.sunatUnavailableFromReinfo')
                  : t('proveedores.wizard.step1.sunatUnavailableManual')}
              </Text>
            </Box>
          )}

          {/* Razon Social: la llena SUNAT; editable sólo si SUNAT no respondió y el REINFO no la trae */}
          <Box>
            <FormLabel tid="proveedores.wizard.step1.legalNameLabel" required />
            {sunatUnavailable === 'manual' ? (
              <Input {...register('legalName')} maxLength={CompanyFieldLengths.legalName} />
            ) : (
              <Input {...register('legalName')} placeholder={t('proveedores.wizard.step1.legalNamePlaceholder')} readOnly disabled opacity={0.7} maxLength={CompanyFieldLengths.legalName} />
            )}
            <CharCounter name="legalName" max={CompanyFieldLengths.legalName} />
          </Box>

          {/* Nombre Comercial */}
          <TrackedInput
            name="commercialName" labelTid="proveedores.wizard.step1.commercialNameLabel" placeholderTid="proveedores.wizard.step1.commercialNamePlaceholder"
            maxLength={CompanyFieldLengths.commercialName} disabled={sunatFields.has('commercialName')}
          />

          {/* Actividades Economicas */}
          <Box gridColumn={{ md: 'span 2' }}>
            <FormLabel tid="proveedores.wizard.step1.economicActivitiesLabel" />
            {economicActivities.length > 0 ? (
              <VStack gap={1} alignItems="stretch">
                {economicActivities.map((act, idx) => (
                  <Box key={idx} p={2} bg={c.bgHover} borderRadius="lg">
                    <Text fontSize="sm" color={c.text}>{act}</Text>
                  </Box>
                ))}
              </VStack>
            ) : (
              <Text tid="proveedores.wizard.step1.economicActivitiesEmpty" fontSize="sm" color={c.textMuted} p={2} />
            )}
          </Box>

          {/* SUNAT info */}
          {sunatConsulted && (
            <>
              <Box>
                <FormLabel tid="proveedores.wizard.step1.taxpayerStatusLabel" />
                <Box p="0.625rem 0.75rem" bg={c.bgInput} borderRadius="sm"><Text fontSize="sm" color={c.text}>{taxpayerStatus ?? '-'}</Text></Box>
              </Box>
              <Box>
                <FormLabel tid="proveedores.wizard.step1.taxpayerConditionLabel" />
                <Box p="0.625rem 0.75rem" bg={c.bgInput} borderRadius="sm"><Text fontSize="sm" color={c.text}>{taxpayerCondition ?? '-'}</Text></Box>
              </Box>
              <Box gridColumn={{ md: 'span 2' }}>
                <FormLabel tid="proveedores.wizard.step1.taxpayerTypeLabel" />
                <Box p="0.625rem 0.75rem" bg={c.bgInput} borderRadius="sm"><Text fontSize="sm" color={c.text}>{taxpayerType ?? '-'}</Text></Box>
              </Box>
            </>
          )}

          {/* Direccion Fiscal */}
          <Box gridColumn={{ md: 'span 2' }}>
            <TrackedInput name="taxAddress" labelTid="proveedores.wizard.step1.taxAddressLabel" placeholderTid="proveedores.wizard.step1.taxAddressPlaceholder" maxLength={CompanyFieldLengths.taxAddress} disabled={sunatFields.has('taxAddress')} />
          </Box>

          {/* Ubigeo */}
          <Box gridColumn={{ md: 'span 2' }}>
            <Controller name="ubigeoCode" control={control} render={({ field }) => (
              <UbigeoSelect value={field.value ?? ''} onChange={field.onChange} hideLabel />
            )} />
          </Box>

          {/*
            TrackedPhoneInput ya sanitiza en vivo y fuerza el teclado numérico; la regla de
            formato (9 dígitos) vive en el schema, no acá. El maxLength sigue saliendo de
            fieldLengths porque espeja la columna de la base, no la regla de negocio.
          */}
          <TrackedPhoneInput
            name="mainPhone"
            labelTid="proveedores.wizard.step1.phoneLabel"
            placeholderTid="proveedores.wizard.step1.phonePlaceholder"
            maxLength={CompanyFieldLengths.mainPhone}
          />

          <TrackedInput name="mainEmail" type="email" labelTid="proveedores.wizard.step1.emailLabel" placeholderTid="proveedores.wizard.step1.emailPlaceholder" maxLength={CompanyFieldLengths.mainEmail} />
          <TrackedInput name="website" labelTid="proveedores.wizard.step1.websiteLabel" placeholderTid="proveedores.wizard.step1.websitePlaceholder" maxLength={CompanyFieldLengths.website} />
        </Grid>
      </FormSection>

      <ContactosSection
        companyId={companyId} persons={persons} onPersonsChange={onPersonsChange}
        addPerson={addPerson} removePerson={removePerson}
        onPendingContactAdd={onPendingContactAdd} onPendingContactRemove={onPendingContactRemove}
        description={t('proveedores.wizard.step1.contactosDescription')}
      />
    </VStack>
  )
}

const IdentityFormInner = withFormProvider(IdentityFormInnerComponent, useStep1IdentityForm)

// ── Readonly view ──

function IdentityReadonly({ defaultValues, initialConsultaData, companyId, persons, onPersonsChange, addPerson, removePerson }: Step1IdentityProps) {
  const c = useFormColors()
  const { t } = useT()
  const v = defaultValues ?? {}
  const consulta = initialConsultaData ?? {}

  return (
    <VStack gap={8} alignItems="stretch">
      <FormSection icon={Building2} title={t('proveedores.wizard.step1.sectionTitle')}>
        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
          <Box><FormLabel tid="proveedores.wizard.step1.rucLabel" required /><Input value={v.ruc ?? ''} readOnly disabled opacity={0.7} /></Box>
          <Box><FormLabel tid="proveedores.wizard.step1.legalNameLabel" required /><Input value={v.legalName ?? ''} readOnly disabled opacity={0.7} /></Box>
          <Box><FormLabel tid="proveedores.wizard.step1.commercialNameLabel" /><Input value={v.commercialName ?? ''} readOnly disabled opacity={0.7} /></Box>
          <Box gridColumn={{ md: 'span 2' }}>
            <FormLabel tid="proveedores.wizard.step1.economicActivitiesLabel" />
            {(consulta.economicActivities?.length ?? 0) > 0 ? (
              <VStack gap={1} alignItems="stretch">
                {consulta.economicActivities!.map((act, idx) => (
                  <Box key={idx} p={2} bg={c.bgHover} borderRadius="lg"><Text fontSize="sm" color={c.text}>{act}</Text></Box>
                ))}
              </VStack>
            ) : (<Text fontSize="sm" color={c.textMuted} p={2}>-</Text>)}
          </Box>
          {consulta.taxpayerStatus && (
            <>
              <Box><FormLabel tid="proveedores.wizard.step1.taxpayerStatusLabel" /><Box p="0.625rem 0.75rem" bg={c.bgInput} borderRadius="sm"><Text fontSize="sm" color={c.text}>{consulta.taxpayerStatus}</Text></Box></Box>
              <Box><FormLabel tid="proveedores.wizard.step1.taxpayerConditionLabel" /><Box p="0.625rem 0.75rem" bg={c.bgInput} borderRadius="sm"><Text fontSize="sm" color={c.text}>{consulta.taxpayerCondition ?? '-'}</Text></Box></Box>
              <Box gridColumn={{ md: 'span 2' }}><FormLabel tid="proveedores.wizard.step1.taxpayerTypeLabel" /><Box p="0.625rem 0.75rem" bg={c.bgInput} borderRadius="sm"><Text fontSize="sm" color={c.text}>{consulta.taxpayerType ?? '-'}</Text></Box></Box>
            </>
          )}
          {consulta.sinCapacidadOperativa && (
            <Box gridColumn={{ md: 'span 2' }} p={3} borderRadius="sm"
              bg={consulta.sinCapacidadOperativa.found ? 'rgba(200, 68, 47, 0.10)' : 'rgba(42, 138, 135, 0.10)'}
              border="1px solid" borderColor={consulta.sinCapacidadOperativa.found ? 'rgba(200, 68, 47, 0.4)' : 'rgba(42, 138, 135, 0.4)'}>
              <Text fontSize="sm" fontWeight="700" color={consulta.sinCapacidadOperativa.found ? '#9a3020' : '#1d6664'}>
                {consulta.sinCapacidadOperativa.found
                  ? t('proveedores.wizard.step1.sscoWarningTitle')
                  : t('proveedores.wizard.step1.sscoClearTitle')}
              </Text>
              <Text fontSize="xs" color={c.textMuted} mt={1}>
                {consulta.sinCapacidadOperativa.padronDate
                  ? t('proveedores.wizard.step1.sscoCheckedAt', { date: fmtSscoDate(consulta.sinCapacidadOperativa.checkedAt), padron: fmtSscoDate(consulta.sinCapacidadOperativa.padronDate) })
                  : t('proveedores.wizard.step1.sscoCheckedAtNoPadron', { date: fmtSscoDate(consulta.sinCapacidadOperativa.checkedAt) })}
              </Text>
            </Box>
          )}
          <Box gridColumn={{ md: 'span 2' }}><FormLabel tid="proveedores.wizard.step1.taxAddressLabel" /><Input value={v.taxAddress ?? ''} readOnly disabled opacity={0.7} /></Box>
          <Box gridColumn={{ md: 'span 2' }}><UbigeoSelect value={v.ubigeoCode ?? ''} onChange={() => {}} disabled hideLabel /></Box>
          <Box><FormLabel tid="proveedores.wizard.step1.phoneLabel" /><Input value={v.mainPhone ?? ''} readOnly disabled opacity={0.7} /></Box>
          <Box><FormLabel tid="proveedores.wizard.step1.emailLabel" /><Input value={v.mainEmail ?? ''} readOnly disabled opacity={0.7} /></Box>
          <Box><FormLabel tid="proveedores.wizard.step1.websiteLabel" /><Input value={v.website ?? ''} readOnly disabled opacity={0.7} /></Box>
        </Grid>
      </FormSection>
      <ContactosSection readonly companyId={companyId} persons={persons} onPersonsChange={onPersonsChange} addPerson={addPerson} removePerson={removePerson} description={t('proveedores.wizard.step1.contactosDescriptionReadonly')} />
    </VStack>
  )
}

// ── Main export ──

export type { Step1IdentityProps, Step1IdentityFormValues }

export default function Step1Identity({ ref, ...props }: Step1IdentityProps) {
  if (props.mode === 'readonly') return <IdentityReadonly {...props} />
  return (
    <IdentityFormInner
      ref={ref}
      defaultValues={props.defaultValues as Step1IdentityFormValues | undefined}
      onConsultaCompleted={props.onConsultaCompleted}
      initialConsultaData={props.initialConsultaData}
      companyId={props.companyId}
      persons={props.persons}
      onPersonsChange={props.onPersonsChange}
      addPerson={props.addPerson}
      removePerson={props.removePerson}
      onPendingContactAdd={props.onPendingContactAdd}
      onPendingContactRemove={props.onPendingContactRemove}
      mode={props.mode}
    />
  )
}
